/**
 * Sleeve / Page Fit Search
 *
 * Client-side "closest fit" finder. The section renders every candidate
 * product as a hidden-until-searched card carrying its full title in
 * data-fitsearch-title. This script:
 *   1. Parses a WxHmm size out of each product title/description (regex).
 *   2. Takes the card size the shopper enters (width + height in mm).
 *   3. Ranks sleeves by how tightly they fit that card, auto-selects the
 *      closest one, and lets the shopper pick a different sleeve.
 *   4. Ranks pages and binders against the SELECTED SLEEVE (a page pocket
 *      must hold the sleeve, not the bare card), re-ranking on selection.
 *      A "no sleeve" pick in step 1 skips the sleeve entirely and ranks
 *      pages against the bare item size instead.
 *
 * Orientation-agnostic: a 63x88 card matches a 88x63 sleeve.
 * Progressive enhancement: with JS off, all cards stay visible as a
 * normal browse grid.
 */
(function () {
  'use strict';

  var NUM = '(\\d{2,3}(?:\\.\\d)?)';
  var XSEP = '\\s*[x×X]\\s*';
  var PAIR = NUM + XSEP + NUM + '\\s*mm';
  // Any WxHmm pair (case-insensitive, so uppercase "MM" in titles is caught).
  var SIZE_RE = new RegExp(PAIR, 'i');
  // The size a product is DESIGNED TO HOLD — the number that matters for fit.
  // Two ways Meowcafe writes it:
  //   a) right before "sleeve": "Fits 73x103mm sleeve", "66x91mm Double Sleeve"
  //   b) after a fit/for/designed cue: "Designed for 200x280mm Postcard",
  //      "For 130 x 170mm Photo", "(FIT 66X91MM)"
  // Both deliberately skip the product's own "Page Size:" and inch toploader specs.
  // Allows brand words between size and "sleeve" ("66x91mm Dragon Shield
  // Sleeve") but not digits/brackets/punctuation, so it anchors to the LAST
  // size before "sleeve" and can't jump across "[67x92MM] … Card Sleeve".
  var TARGET_BEFORE_SLEEVE = new RegExp(PAIR + '[^.,;)\\]\\[\\n\\d]{0,25}?sleeve', 'i');
  var TARGET_AFTER_CUE = new RegExp('\\b(?:fits?|holds?|for|designed)\\b[^.)\\n]{0,40}?' + PAIR, 'i');

  function toPair(m) {
    if (!m) return null;
    var a = parseFloat(m[1]);
    var b = parseFloat(m[2]);
    if (!a || !b) return null;
    // Normalise to [short, long] so orientation never matters.
    return a <= b ? [a, b] : [b, a];
  }

  function parseSize(text) {
    return text ? toPair(String(text).match(SIZE_RE)) : null;
  }

  function parseTarget(text) {
    if (!text) return null;
    text = String(text);
    return toPair(text.match(TARGET_BEFORE_SLEEVE)) || toPair(text.match(TARGET_AFTER_CUE));
  }

  // Strict variant: only a size explicitly tied to the word "sleeve".
  function parseSleeveTarget(text) {
    return text ? toPair(String(text).match(TARGET_BEFORE_SLEEVE)) : null;
  }

  // Resolve two sizes for a product:
  //   compute — the size we measure fit against (must clear the card by a real gap)
  //   display — the friendly size to show in the "Fits …" line
  // Sleeves/binders: fit is measured against their OWN outer size (the [63x92MM]
  //   in the title); the designed-for card size, if stated, is nicer to display.
  // Pages: fit is measured against the pocket's own dimensions from the
  //   title ("1 Pocket - 222x314mm"); an explicit sleeve callout ("Fits …
  //   66x91mm Dragon Shield Sleeve") is kept as `stated` — the sleeve this
  //   pocket was designed for — and marks a perfect fit when it matches the
  //   selected sleeve, plus makes a friendlier "Holds …" line.
  function resolve(title, desc, role) {
    var own = parseSize(title);
    var compute, display, stated = null;
    if (role === 'page') {
      stated = parseSleeveTarget(title) || parseSleeveTarget(desc);
      compute = own || stated || parseSize(desc);
      display = stated || compute;
    } else {
      var target = parseTarget(desc) || parseTarget(title);
      compute = own || target || parseSize(desc);
      display = target || own || parseSize(desc);
      // "Fits 61x91" on a 61x91mm sleeve reads as the card size it takes —
      // wrong by the clearance rule. A stated designed-for size is only a
      // real card size if it clears the sleeve by MIN_FIT per side (SD-04's
      // "Perfect Fit for 55x85" qualifies; SM-05's "For Fit into 69x93mm
      // Page" is a page, not a card). Otherwise show the derived max card:
      // own size − MIN_FIT per side (a 61x91 sleeve fits up to 59x89).
      if (role === 'sleeve' && display && compute
          && !(display[0] <= compute[0] - MIN_FIT && display[1] <= compute[1] - MIN_FIT)) {
        display = [compute[0] - MIN_FIT, compute[1] - MIN_FIT];
      }
    }
    return compute ? { compute: compute, display: display || compute, stated: stated } : null;
  }

  function fmtSize(size) {
    // size stored as [short, long]; show as WxH using the original-ish order.
    return size[0] + '×' + size[1] + 'mm';
  }

  // The card's size caption: sleeves state the max card they take, pages
  // the sleeve their pockets hold.
  function sizeLine(card) {
    if (isBundle(card)) return '';
    if (card.holdsText) return card.holdsText;
    if (!card.displaySize) return '';
    if (card.role === 'binder') return '';
    var prefix = card.role === 'page' ? 'Holds up to '
      : (card.role === 'sleeve' ? 'Fits up to ' : 'Fits ');
    return prefix + fmtSize(card.displaySize);
  }

  function shortTitle(card) {
    var t = card.titleText || '';
    return t.length > 50 ? t.slice(0, 47) + '…' : t;
  }

  // Variety packs / bundles are upsells, not the canonical product for a
  // size — they display AFTER the regular products and never win auto-select
  // over one ("[Valued Set] … Variety Pack of 4 Styles", "(Valued Bundle) …",
  // "400 Sleeves Double Sleeving Bundle Set").
  var BUNDLE_RE = /\bbundle\b|valued\s+set|variety\s+pack|value\s+pack/i;
  function isBundle(card) {
    return BUNDLE_RE.test(card.titleText || '');
  }

  // True when a map key like "W-15" or "SE-27" identifies this product:
  // exact variant-SKU match, or the key appears as a whole token in the SKU
  // or title. Token boundaries stop "W-02" from matching "W-02-TL".
  function skuMatches(key, card) {
    key = String(key).toUpperCase();
    if (!key) return false;
    if (card.sku && card.sku.toUpperCase() === key) return true;
    var hay = ((card.sku || '') + ' ' + (card.titleText || '')).toUpperCase();
    var i = hay.indexOf(key);
    while (i !== -1) {
      var before = i > 0 ? hay.charAt(i - 1) : '';
      var after = hay.charAt(i + key.length);
      if (!/[A-Z0-9-]/.test(before) && !/[A-Z0-9-]/.test(after)) return true;
      i = hay.indexOf(key, i + 1);
    }
    return false;
  }

  // Merchant card-size → sleeve-size preferences (map "_cardSleeves":
  // {"55x85": "58x89"}). For a matching search, only sleeves of exactly the
  // preferred size count as perfect — the merchant's word beats clearance
  // math (a 57x88 measures snugger on a 55x85 photocard, but the store's
  // perfect-fit sleeve for it is the 58x89).
  var LOOSE_SIZE_RE = /^(\d{2,3}(?:\.\d)?)\s*[x×X]\s*(\d{2,3}(?:\.\d)?)\s*(?:mm)?$/i;
  function parseLooseSize(text) {
    return text ? toPair(String(text).trim().match(LOOSE_SIZE_RE)) : null;
  }
  function findCardSleevePref(map, tShort, tLong) {
    var prefs = map && map._cardSleeves;
    if (!prefs || typeof prefs !== 'object') return null;
    for (var key in prefs) {
      if (!Object.prototype.hasOwnProperty.call(prefs, key)) continue;
      if (key.charAt(0) === '_') continue;
      var k = parseLooseSize(key);
      if (k && Math.abs(k[0] - tShort) <= 0.5 && Math.abs(k[1] - tLong) <= 0.5) {
        return parseLooseSize(prefs[key]);
      }
    }
    return null;
  }

  // Find the manual-map entry for the selected sleeve. Keys starting with
  // an underscore are documentation/disabled examples.
  function findMapEntry(map, sleeveCard) {
    for (var key in map) {
      if (!Object.prototype.hasOwnProperty.call(map, key)) continue;
      if (key.charAt(0) === '_') continue;
      if (skuMatches(key, sleeveCard)) return map[key];
    }
    return null;
  }

  // Fetch the optional manual match map (assets/sleeve-fit-map.json).
  function loadMap(root, onLoad) {
    var url = root.getAttribute('data-fitsearch-map');
    if (!url || typeof fetch !== 'function') return;
    fetch(url)
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        if (data && typeof data === 'object' && !Array.isArray(data)) onLoad(data);
      })
      .catch(function () { /* no or invalid map — automatic matching only */ });
  }

  // A sleeve must clear the card by at least MIN_FIT mm on BOTH sides to
  // physically fit, and by no more than PERFECT_MAX mm per side to be "perfect".
  // (e.g. a 58x89 card → 61x91 sleeve is +3/+2 = perfect; a 58x89 sleeve is
  // +0/+0 and won't fit; a 63x92 sleeve is +5/+3 and is only a good fit.)
  var MIN_FIT = 2;
  var PERFECT_MAX = 4;
  // Page pockets are cut a touch over the sleeve they're designed for (a
  // 63x93mm pocket holds a 61x91mm sleeve), so vs the selected sleeve a
  // pocket fits from 0mm clearance and is "perfect" up to 5mm per side.
  var POCKET_MIN_FIT = 0;
  var POCKET_PERFECT_MAX = 5;
  // Past this much total slack an item is "Loose" — too sloppy to recommend
  // when anything snugger exists.
  var LOOSE_SLACK = 50;
  // Slack bands scale with the target size: the absolute cutoffs are tuned
  // for ~63x88mm cards (24 ≈ 16% and 50 ≈ 33% of W+H) but would call a
  // 270x150mm postcard with 10mm of clearance "Loose", so larger targets
  // use the percentage instead.
  function goodSlackMax(base) { return Math.max(24, 0.16 * base); }
  function looseSlackMax(base) { return Math.max(LOOSE_SLACK, 0.33 * base); }

  // Binder compatibility is a FAMILY rule, not a size rule: a binder series
  // holds specific page series (hole pattern / page width). Page family →
  // binder families that hold it.
  var FAMILY_BINDERS = {
    'a4-plus': ['a4-plus'],
    'a4-slim': ['a4-plus', 'a4-standard'],
    'a4-standard': ['a4-plus', 'a4-standard'],
    'a4-extra-wide': ['a4-extra-wide'],
    'a5-extra-wide': ['a5-extra-wide'],
    'a5-wide': ['a5-wide'],
    'a5-standard': ['a5-wide'],
    'a5-slim': ['a5-wide']
  };

  // Score a product (measured against its own size) vs the target size.
  function score(tShort, tLong, prod, minFit) {
    var dShort = prod[0] - tShort;
    var dLong = prod[1] - tLong;
    var fits = dShort >= minFit && dLong >= minFit;
    var slack = dShort + dLong; // total extra mm across both dims
    // Fitting products always outrank the rest; among fitting, tightest first.
    var score = fits ? slack : 1000 + Math.abs(dShort) + Math.abs(dLong);
    return { fits: fits, dShort: dShort, dLong: dLong, slack: slack, score: score };
  }

  // Perfect when within the per-side window, or when the product's stated
  // designed-for sleeve matches the target within a millimetre either way.
  function isPerfect(card, res, tShort, tLong, perfectMax) {
    if (!res.fits) return false;
    if (card.perfectSize) {
      return Math.abs(card.perfectSize[0] - tShort) <= 0.5
        && Math.abs(card.perfectSize[1] - tLong) <= 0.5;
    }
    if (card.stated
        && Math.abs(card.stated[0] - tShort) <= 1
        && Math.abs(card.stated[1] - tLong) <= 1) return true;
    return res.dShort <= perfectMax && res.dLong <= perfectMax;
  }

  function badgeFor(res, perfect, base) {
    if (!res.fits) {
      // Smaller than the card on a side, or too snug to actually slide in.
      return (res.dShort < 0 || res.dLong < 0)
        ? { text: 'Too small', kind: 'tight' }
        : { text: 'Too tight', kind: 'tight' };
    }
    if (perfect) return { text: 'Perfect fit', kind: 'perfect' };
    if (res.slack <= goodSlackMax(base)) return { text: 'Good fit', kind: 'great' };
    return { text: 'Loose', kind: 'roomy' };
  }

  function buildCards(root) {
    var cards = [];
    var nodes = root.querySelectorAll('[data-fitsearch-card]');
    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      var role = el.getAttribute('data-fitsearch-role');
      var r = resolve(
        el.getAttribute('data-fitsearch-title'),
        el.getAttribute('data-fitsearch-desc'),
        role
      );
      cards.push({
        el: el,
        role: role,
        // Position in the source collection — the collection's sort order
        // (set it to "Best selling" in admin) doubles as the sales rank.
        rank: parseInt(el.getAttribute('data-fitsearch-rank'), 10) || 9999,
        sku: el.getAttribute('data-fitsearch-sku') || '',
        titleText: el.getAttribute('data-fitsearch-title') || '',
        size: r ? r.compute : null,
        displaySize: r ? r.display : null,
        stated: r ? r.stated : null,
        sizeEl: el.querySelector('[data-fitsearch-size]'),
        badgeEl: el.querySelector('[data-fitsearch-badge]'),
        selectEl: el.querySelector('[data-fitsearch-select]')
      });
      el.setAttribute('hidden', '');
    }
    return cards;
  }

  function setSelected(card, on) {
    card.el.classList.toggle('fitsearch-card--selected', on);
    if (card.selectEl) {
      card.selectEl.textContent = card.role === 'page'
        ? (on ? 'Selected page ✓' : 'Match binders to this page')
        : (on ? 'Selected ✓ · Tap to unselect' : 'Match pages to this sleeve');
    }
  }

  // Show one group's cards browse-style: everything visible, no badges,
  // select buttons available so the chain can start from any sleeve/page.
  function browseGroup(group, cards) {
    var count = 0;
    for (var i = 0; i < cards.length; i++) {
      var c = cards[i];
      if (c.el.getAttribute('data-fitsearch-group-id') !== group.id) continue;
      c.el.removeAttribute('hidden');
      if (c.badgeEl) c.badgeEl.setAttribute('hidden', '');
      if (c.selectEl) c.selectEl.removeAttribute('hidden');
      setSelected(c, false);
      if (c.sizeEl) c.sizeEl.textContent = sizeLine(c);
      count++;
    }
    if (group.emptyEl) group.emptyEl.setAttribute('hidden', '');
    group.el.removeAttribute('hidden');
    return count;
  }

  function resetView(root, cards, groups) {
    for (var i = 0; i < cards.length; i++) {
      cards[i].el.removeAttribute('hidden');
      if (cards[i].badgeEl) cards[i].badgeEl.setAttribute('hidden', '');
      // Keep select buttons visible in browse mode — a shopper can start the
      // sleeve → page → binder chain without entering a card size.
      if (cards[i].selectEl) cards[i].selectEl.removeAttribute('hidden');
      setSelected(cards[i], false);
      if (cards[i].sizeEl) {
        cards[i].sizeEl.textContent = sizeLine(cards[i]);
      }
    }
    var hasBinders = false;
    for (var g = 0; g < groups.length; g++) {
      groups[g].el.removeAttribute('hidden');
      if (groups[g].role === 'binder') hasBinders = true;
      groups[g].emptyEl && groups[g].emptyEl.setAttribute('hidden', '');
      if (groups[g].noteEl) {
        groups[g].noteEl.textContent = groups[g].noteDefault;
        if (groups[g].noteDefault) groups[g].noteEl.removeAttribute('hidden');
        else groups[g].noteEl.setAttribute('hidden', '');
      }
    }
    // Drives visibility of the binder step during a search.
    if (hasBinders) root.setAttribute('data-fitsearch-has-binders', '');
    else root.removeAttribute('data-fitsearch-has-binders');
  }

  // Rank one group's cards against a target size and reveal the top matches.
  // Returns the ranked list of fitting entries ({card, res}) that were shown.
  function runGroup(group, cards, tShort, tLong, minFit, perfectMax, opts) {
    opts = opts || {};
    var max = parseInt(group.el.getAttribute('data-fitsearch-max'), 10) || 6;

    var ranked = [];
    for (var i = 0; i < cards.length; i++) {
      var c = cards[i];
      if (c.el.getAttribute('data-fitsearch-group-id') !== group.id) continue;
      c.el.setAttribute('hidden', '');
      if (c.selectEl) c.selectEl.setAttribute('hidden', '');
      setSelected(c, false);
      if (!c.size) continue;
      // holdsSize: merchant override for adapter sleeves — the card size the
      // sleeve is built to hold (E-07 measures 58x89 outside but only takes
      // a 35x45 Mini ID). Fit against it exactly, from 0mm clearance.
      var res = c.holdsSize
        ? score(tShort, tLong, c.holdsSize, POCKET_MIN_FIT)
        : score(tShort, tLong, c.size, minFit);
      var perfect;
      if (opts.preferSize && !c.holdsSize) {
        // A merchant-preferred sleeve size exists for this exact card size:
        // only that size is "perfect", however snug the others measure — and
        // it counts as fitting even when the clearance math calls it too
        // tight (a 61x91 double-sleeve search pins the 61x91 outer sleeves).
        // Adapter sleeves (holdsSize) stay judged by what they hold — an
        // outer size matching the pin doesn't make a Mini ID slot fit.
        perfect = Math.abs(c.size[0] - opts.preferSize[0]) <= 0.5
          && Math.abs(c.size[1] - opts.preferSize[1]) <= 0.5;
        if (perfect && !res.fits) {
          res.fits = true;
          res.score = res.slack;
        }
      } else {
        perfect = isPerfect(c, res, tShort, tLong, perfectMax);
      }
      ranked.push({ card: c, res: res, perfect: perfect });
    }

    ranked.sort(function (a, b) { return a.res.score - b.res.score; });

    var base = tShort + tLong;
    var perfect = [];
    var snug = [];
    for (var p = 0; p < ranked.length; p++) {
      if (ranked[p].perfect) perfect.push(ranked[p]);
      if (ranked[p].res.fits && (ranked[p].perfect || ranked[p].res.slack <= looseSlackMax(base))) snug.push(ranked[p]);
    }
    var perfectMode = perfect.length > 0;

    // Sleeves: if any perfect fit exists, show ONLY perfect fits — no
    //   looser alternatives.
    // Pages/binders (showAllFits): a shopper wants EVERY pocket that holds
    //   the sleeve, so keep good fits alongside perfect ones, best fit
    //   first. With nothing snug, fall back to fitting-but-loose options.
    var pool;
    // Products that don't fit are never shown — an empty group with its
    // "no matches" message beats a grid of "Too small" items.
    var fitting = [];
    for (var f = 0; f < ranked.length; f++) {
      if (ranked[f].res.fits) fitting.push(ranked[f]);
    }
    if (opts.showAllFits) {
      if (snug.length > 0 || opts.noFallback) {
        // noFallback: an empty snug list stays empty — the caller decides
        // whether to re-run with the fitting fallback (only when NO group
        // anywhere has a snug fit, so one group's perfect fits aren't
        // diluted by another group's loose ones).
        // Bundles last; otherwise best fit first (perfect, then tightest),
        // best-selling order breaking ties.
        pool = snug.slice().sort(function (a, b) {
          var ab = isBundle(a.card), bb = isBundle(b.card);
          if (ab !== bb) return ab ? 1 : -1;
          if (a.perfect !== b.perfect) return a.perfect ? -1 : 1;
          if (a.res.slack !== b.res.slack) return a.res.slack - b.res.slack;
          return a.card.rank - b.card.rank;
        });
      } else {
        pool = fitting;
      }
    } else {
      // Sleeves: bundles sort last here too, then tightest fit first.
      pool = (perfectMode ? perfect : fitting).slice().sort(function (a, b) {
        var ab = isBundle(a.card), bb = isBundle(b.card);
        if (ab !== bb) return ab ? 1 : -1;
        return a.res.score - b.res.score;
      });
    }
    var shown = pool.slice(0, max);

    for (var j = 0; j < shown.length; j++) {
      var card = shown[j].card;
      var res = shown[j].res;
      var badge = badgeFor(res, shown[j].perfect, base);
      // A fitting bundle's size verdict ("Roomy") reads like a knock — what
      // matters is that the set contains a matching item.
      if (res.fits && isBundle(card)) {
        badge = {
          text: card.role === 'page' ? 'Includes this page' : 'Includes this sleeve',
          kind: 'bundle'
        };
      }
      card.el.removeAttribute('hidden');
      // Re-order DOM so best fit renders first.
      group.gridEl.appendChild(card.el);
      if (card.badgeEl) {
        card.badgeEl.textContent = badge.text;
        card.badgeEl.setAttribute('data-fit-kind', badge.kind);
        card.badgeEl.removeAttribute('hidden');
      }
      if (card.sizeEl) {
        card.sizeEl.textContent = isBundle(card)
          ? ''
          : card.holdsText
          ? card.holdsText
          : opts.sizePrefix
          ? opts.sizePrefix + fmtSize(card.displaySize)
          : sizeLine(card);
      }
      // Fitting sleeves are selectable — picking one re-ranks pages/binders.
      if (card.selectEl && res.fits) card.selectEl.removeAttribute('hidden');
    }

    if (group.emptyEl) {
      if (shown.length === 0) group.emptyEl.removeAttribute('hidden');
      else group.emptyEl.setAttribute('hidden', '');
    }

    var shownFitting = [];
    for (var k = 0; k < shown.length; k++) {
      if (shown[k].res.fits) shownFitting.push(shown[k]);
    }
    return { shownCount: shown.length, fitting: shownFitting };
  }

  // Show exactly the hand-picked SKUs for this group, in map order, badged
  // "Recommended" — the merchant's word beats the size math.
  function runMapped(group, cards, skus, opts) {
    opts = opts || {};
    var groupCards = [];
    for (var i = 0; i < cards.length; i++) {
      var c = cards[i];
      if (c.el.getAttribute('data-fitsearch-group-id') !== group.id) continue;
      c.el.setAttribute('hidden', '');
      if (c.selectEl) c.selectEl.setAttribute('hidden', '');
      setSelected(c, false);
      groupCards.push(c);
    }

    var shownCards = [];
    for (var s = 0; s < skus.length; s++) {
      for (var j = 0; j < groupCards.length; j++) {
        var card = groupCards[j];
        if (!card.el.hasAttribute('hidden')) continue; // already shown
        if (!skuMatches(skus[s], card)) continue;
        card.el.removeAttribute('hidden');
        group.gridEl.appendChild(card.el);
        if (card.badgeEl) {
          card.badgeEl.textContent = 'Recommended';
          card.badgeEl.setAttribute('data-fit-kind', 'perfect');
          card.badgeEl.removeAttribute('hidden');
        }
        if (card.sizeEl) {
          card.sizeEl.textContent = isBundle(card)
            ? ''
            : card.holdsText
            ? card.holdsText
            : card.displaySize && opts.sizePrefix
            ? opts.sizePrefix + fmtSize(card.displaySize)
            : sizeLine(card);
        }
        if (card.selectEl) card.selectEl.removeAttribute('hidden');
        shownCards.push(card);
        break;
      }
    }

    if (group.emptyEl) {
      if (shownCards.length === 0) group.emptyEl.removeAttribute('hidden');
      else group.emptyEl.setAttribute('hidden', '');
    }
    return { shownCount: shownCards.length, shown: shownCards };
  }

  // Show a binder group's products as-is: every binder in a family group
  // holds the matched pages by construction, so there's no size ranking —
  // just collection (best-selling) order.
  function runCompatible(group, cards, opts) {
    opts = opts || {};
    var max = parseInt(group.el.getAttribute('data-fitsearch-max'), 10) || 6;
    var groupCards = [];
    for (var i = 0; i < cards.length; i++) {
      var c = cards[i];
      if (c.el.getAttribute('data-fitsearch-group-id') !== group.id) continue;
      c.el.setAttribute('hidden', '');
      groupCards.push(c);
    }
    groupCards.sort(function (a, b) { return a.rank - b.rank; });

    var shownCount = 0;
    for (var j = 0; j < groupCards.length && shownCount < max; j++) {
      var card = groupCards[j];
      card.el.removeAttribute('hidden');
      group.gridEl.appendChild(card.el);
      if (card.badgeEl) {
        card.badgeEl.textContent = 'Compatible';
        card.badgeEl.setAttribute('data-fit-kind', 'perfect');
        card.badgeEl.removeAttribute('hidden');
      }
      if (card.sizeEl) card.sizeEl.textContent = '';
      shownCount++;
    }
    if (group.emptyEl) group.emptyEl.setAttribute('hidden', '');
    return shownCount;
  }

  function init(root) {
    var form = root.querySelector('[data-fitsearch-form]');
    if (!form) return;
    var wInput = form.querySelector('[data-fitsearch-width]');
    var hInput = form.querySelector('[data-fitsearch-height]');
    var statusEl = root.querySelector('[data-fitsearch-status]');

    var cards = buildCards(root);
    var groups = [];
    var groupNodes = root.querySelectorAll('[data-fitsearch-group]');
    for (var i = 0; i < groupNodes.length; i++) {
      var noteEl = groupNodes[i].querySelector('[data-fitsearch-note]');
      groups.push({
        el: groupNodes[i],
        id: groupNodes[i].getAttribute('data-fitsearch-group-id'),
        role: groupNodes[i].getAttribute('data-fitsearch-role'),
        family: groupNodes[i].getAttribute('data-fitsearch-family') || '',
        gridEl: groupNodes[i].querySelector('[data-fitsearch-grid]'),
        emptyEl: groupNodes[i].querySelector('[data-fitsearch-empty]'),
        noteEl: noteEl,
        noteDefault: noteEl ? noteEl.textContent : ''
      });
    }

    // The sleeve and page the shopper picked (card objects); cleared on
    // reset/new search, revalidated on every re-run.
    var selectedSleeve = null;
    var selectedPage = null;
    // True after the shopper unselects a sleeve: show the full sleeve list
    // and don't auto-select until they pick one themselves.
    var sleeveExpanded = false;
    // True when the shopper chose "no sleeve": step 1 collapses and pages
    // rank against the entered item size instead of a sleeve.
    var noSleeve = false;
    // True when the current sleeve was picked BY THE SHOPPER (not
    // auto-selected): only then does step 1 collapse to that one card.
    var sleeveManual = false;

    // "No sleeve" option: only meaningful during a size search, so it's
    // hidden in browse mode and shown alongside the step-1 results.
    var noSleeveBtn = root.querySelector('[data-fitsearch-nosleeve]');
    var noSleeveLabel = noSleeveBtn ? noSleeveBtn.textContent : '';
    function setNoSleeveUI(visible, on) {
      if (!noSleeveBtn) return;
      if (visible) noSleeveBtn.removeAttribute('hidden');
      else noSleeveBtn.setAttribute('hidden', '');
      noSleeveBtn.classList.toggle('fitsearch-nosleeve--on', !!on);
      noSleeveBtn.textContent = on
        ? (noSleeveBtn.getAttribute('data-fitsearch-nosleeve-selected') || noSleeveLabel)
        : noSleeveLabel;
    }

    // Step-guidance banners: live status lines + a pulse that draws the eye
    // to the next step after a manual pick.
    var stepStatus = {};
    var statusNodes = root.querySelectorAll('[data-fitsearch-step-status]');
    for (var sn = 0; sn < statusNodes.length; sn++) {
      stepStatus[statusNodes[sn].getAttribute('data-fitsearch-step-status')] = statusNodes[sn];
    }
    function setStep(n, text) {
      if (stepStatus[n]) stepStatus[n].textContent = text;
    }
    function pulseStep(n) {
      var el = root.querySelector('[data-fitsearch-step-banner="' + n + '"]');
      if (!el) return;
      el.classList.toggle('fitsearch-step--pulse', false);
      if (typeof el.offsetWidth === 'number') void el.offsetWidth; // restart animation
      el.classList.toggle('fitsearch-step--pulse', true);
      setTimeout(function () { el.classList.toggle('fitsearch-step--pulse', false); }, 2000);
    }

    // Optional manual match map (sleeve SKU → page/binder SKUs). Loads async;
    // re-run any active search once it arrives.
    var fitMap = null;
    loadMap(root, function (map) {
      fitMap = map;
      // A map entry's "holds" size replaces the parsed size for fit matching.
      // "holds_text" can override only the displayed line. Covers adapter sleeves (E-07 measures
      // 58x89 outside but takes a 35x45 Mini ID) and special pages (the
      // double-sided 65x93 pockets are built for 61x91 double sleeves, not
      // bare cards). Binders don't size-match, so they're skipped.
      for (var mc = 0; mc < cards.length; mc++) {
        if (cards[mc].role === 'binder') continue;
        var entry = findMapEntry(map, cards[mc]);
        var holds = entry && parseLooseSize(entry.holds);
        var perfectSize = entry && parseLooseSize(entry.perfect);
        if (holds) {
          cards[mc].holdsSize = holds;
          cards[mc].displaySize = holds;
        }
        if (perfectSize) cards[mc].perfectSize = perfectSize;
        if (entry && entry.holds_text) cards[mc].holdsText = String(entry.holds_text);
      }
      if (root.hasAttribute('data-fitsearch-active')) refresh();
      else resetView(root, cards, groups);
    });

    // Start in browse mode: everything visible, no badges.
    resetView(root, cards, groups);

    function run(cardShort, cardLong) {
      var totalShown = 0;
      var g, group;
      // sizeMode: ranking anchored on an entered card size. Without one the
      // chain starts from a manually picked sleeve (or page) instead.
      var sizeMode = cardShort != null && cardLong != null;

      if (sizeMode && noSleeve) {
        // "No sleeve": the item goes straight into a page pocket. Step 1
        // collapses — every sleeve card and group hides — and the rest of
        // the chain anchors on the entered item size.
        selectedSleeve = null;
        for (g = 0; g < groups.length; g++) {
          if (groups[g].role === 'sleeve') groups[g].el.setAttribute('hidden', '');
        }
        for (var ns = 0; ns < cards.length; ns++) {
          if (cards[ns].role === 'sleeve') cards[ns].el.setAttribute('hidden', '');
        }
      } else if (sizeMode) {
        // 1. Sleeves rank against the card itself. A "_cardSleeves" entry in
        //    the manual map pins the perfect sleeve size for this card size.
        var preferSleeve = fitMap ? findCardSleevePref(fitMap, cardShort, cardLong) : null;
        var fittingSleeves = [];
        for (g = 0; g < groups.length; g++) {
          group = groups[g];
          if (group.role !== 'sleeve') continue;
          // The group container may have been hidden by a previous no-sleeve
          // pick or manual-selection collapse — bring it back for this run.
          group.el.removeAttribute('hidden');
          var out = runGroup(group, cards, cardShort, cardLong, MIN_FIT, PERFECT_MAX,
            { preferSize: preferSleeve });
          totalShown += out.shownCount;
          fittingSleeves = fittingSleeves.concat(out.fitting);
        }

        // 2. Keep the shopper's pick if it still fits; otherwise auto-select
        //    the tightest-fitting sleeve.
        var selectedEntry = null;
        for (var s = 0; s < fittingSleeves.length; s++) {
          if (selectedSleeve && fittingSleeves[s].card === selectedSleeve) {
            selectedEntry = fittingSleeves[s];
            break;
          }
        }
        if (!selectedEntry && fittingSleeves.length > 0 && !sleeveExpanded) {
          selectedEntry = fittingSleeves[0];
          sleeveManual = false;
        }
        selectedSleeve = selectedEntry ? selectedEntry.card : null;
        if (selectedSleeve) setSelected(selectedSleeve, true);
      } else {
        // Sleeve-first: every sleeve stays visible and selectable; the pick
        // anchors the rest of the chain.
        for (g = 0; g < groups.length; g++) {
          if (groups[g].role === 'sleeve') totalShown += browseGroup(groups[g], cards);
        }
        if (selectedSleeve) setSelected(selectedSleeve, true);
      }

      // An AUTO-selected sleeve keeps the full recommended list visible so
      // shoppers can compare; a sleeve the shopper picked themselves
      // collapses the list to that card. Unselecting brings the list back.
      if (selectedSleeve && sleeveManual) {
        for (var sc = 0; sc < cards.length; sc++) {
          if (cards[sc].role === 'sleeve' && cards[sc] !== selectedSleeve) {
            cards[sc].el.setAttribute('hidden', '');
          }
        }
        for (g = 0; g < groups.length; g++) {
          group = groups[g];
          if (group.role !== 'sleeve') continue;
          if (selectedSleeve.el.getAttribute('data-fitsearch-group-id') === group.id) {
            group.el.removeAttribute('hidden');
            if (group.emptyEl) group.emptyEl.setAttribute('hidden', '');
          } else {
            group.el.setAttribute('hidden', '');
          }
        }
      }

      // 3. Pages rank against the SELECTED SLEEVE — a pocket has to hold the
      //    sleeve, not the bare card. Without any fitting sleeve, fall back
      //    to the card size so the groups still show something. Page groups
      //    with no real fit hide entirely (heading and all).
      var tShort = selectedSleeve ? selectedSleeve.size[0] : cardShort;
      var tLong = selectedSleeve ? selectedSleeve.size[1] : cardLong;
      var minFit = selectedSleeve ? POCKET_MIN_FIT : MIN_FIT;
      var perfectMax = selectedSleeve ? POCKET_PERFECT_MAX : PERFECT_MAX;
      var mapEntry = fitMap && selectedSleeve ? findMapEntry(fitMap, selectedSleeve) : null;
      var mappedPages = mapEntry && Array.isArray(mapEntry.pages) ? mapEntry.pages : null;
      var mappedBinders = mapEntry && Array.isArray(mapEntry.binders) ? mapEntry.binders : null;
      // manualOnly: the map is the single source of truth — a sleeve with no
      // entry (or no 'pages' list) recommends nothing, instead of falling
      // back to automatic size matching. Binders still follow the family
      // rule from the selected mapped page unless 'binders' is given.
      if (selectedSleeve && fitMap && fitMap._settings && fitMap._settings.manualOnly && !mappedPages) {
        mappedPages = [];
      }
      var chosenPage = null;
      var pageCandidates = []; // every selectable page, tagged with its family
      if (!sizeMode && !selectedSleeve) {
        // Page-first: no card size and no sleeve pick — pages stay visible
        // and selectable, and a picked page drives the binder step alone.
        for (g = 0; g < groups.length; g++) {
          group = groups[g];
          if (group.role !== 'page') continue;
          totalShown += browseGroup(group, cards);
          if (group.noteEl) {
            group.noteEl.textContent = group.noteDefault;
            if (group.noteDefault) group.noteEl.removeAttribute('hidden');
          }
          if (selectedPage && selectedPage.el.getAttribute('data-fitsearch-group-id') === group.id) {
            chosenPage = { card: selectedPage, family: group.family };
          }
        }
        if (chosenPage) setSelected(selectedPage, true);
        else selectedPage = null;
      } else {
      // First pass: snug fits only. The ranked "closest fit" fallback is a
      // last resort — re-run with it only if NO page group has a snug fit,
      // so one group's perfect matches aren't padded out by another group's
      // loose ones.
      var pageRuns = [];
      var anySnugFit = false;
      for (g = 0; g < groups.length; g++) {
        group = groups[g];
        if (group.role !== 'page') continue;
        var opts = { showAllFits: true, sizePrefix: 'Holds up to ' };
        var fitCount, shownCount, entries;
        if (mappedPages) {
          // A manual map entry for this sleeve overrides the size matching.
          var mres = runMapped(group, cards, mappedPages, opts);
          fitCount = shownCount = mres.shownCount;
          // Map order is the merchant's preference order — keep it as rank.
          entries = [];
          for (var mc = 0; mc < mres.shown.length; mc++) {
            entries.push({ card: mres.shown[mc], perfect: true, slack: mc });
          }
        } else {
          opts.noFallback = true;
          var outPages = runGroup(group, cards, tShort, tLong, minFit, perfectMax, opts);
          fitCount = outPages.fitting.length;
          shownCount = outPages.shownCount;
          entries = outPages.fitting;
        }
        if (fitCount > 0) anySnugFit = true;
        pageRuns.push({ group: group, fitCount: fitCount, shownCount: shownCount, entries: entries });
      }
      if (!mappedPages && !anySnugFit) {
        for (var p2 = 0; p2 < pageRuns.length; p2++) {
          var rerun = runGroup(pageRuns[p2].group, cards, tShort, tLong, minFit, perfectMax,
            { showAllFits: true, sizePrefix: 'Holds up to ' });
          pageRuns[p2].fitCount = rerun.fitting.length;
          pageRuns[p2].shownCount = rerun.shownCount;
          pageRuns[p2].entries = rerun.fitting;
        }
      }
      for (var p3 = 0; p3 < pageRuns.length; p3++) {
        group = pageRuns[p3].group;
        if (pageRuns[p3].fitCount > 0) {
          group.el.removeAttribute('hidden');
          totalShown += pageRuns[p3].shownCount;
          var es = pageRuns[p3].entries;
          for (var c2 = 0; c2 < es.length; c2++) {
            pageCandidates.push({
              card: es[c2].card,
              family: group.family,
              perfect: !!es[c2].perfect,
              slack: es[c2].res ? es[c2].res.slack : es[c2].slack
            });
          }
        } else {
          group.el.setAttribute('hidden', '');
        }
        if (group.noteEl) {
          group.noteEl.textContent = selectedSleeve
            ? 'Matched to your selected ' + fmtSize(selectedSleeve.size) + ' sleeve.'
            : (sizeMode && noSleeve
              ? 'Matched to your ' + cardShort + '×' + cardLong + 'mm item — no sleeve.'
              : group.noteDefault);
          group.noteEl.removeAttribute('hidden');
        }
      }

      // 4. Keep the shopper's page pick if it's still shown; otherwise
      //    auto-select the best page (perfect first, then tightest).
      for (var pc = 0; pc < pageCandidates.length; pc++) {
        if (selectedPage && pageCandidates[pc].card === selectedPage) {
          chosenPage = pageCandidates[pc];
          break;
        }
      }
      if (!chosenPage && pageCandidates.length > 0) {
        pageCandidates.sort(function (a, b) {
          var ab = isBundle(a.card), bb = isBundle(b.card);
          if (ab !== bb) return ab ? 1 : -1;
          if (a.perfect !== b.perfect) return a.perfect ? -1 : 1;
          if (a.slack !== b.slack) return a.slack - b.slack;
          return a.card.rank - b.card.rank;
        });
        chosenPage = pageCandidates[0];
      }
      selectedPage = chosenPage ? chosenPage.card : null;
      if (selectedPage) setSelected(selectedPage, true);
      }

      // 5. Binders follow the SELECTED PAGE: only the binder families that
      //    hold that page's series appear. A manual map's 'binders' list
      //    overrides the rule.
      var binderFamilies = {};
      if (chosenPage && chosenPage.family) {
        var unlocked = FAMILY_BINDERS[chosenPage.family] || [];
        for (var b = 0; b < unlocked.length; b++) binderFamilies[unlocked[b]] = true;
      }
      var anyBinderShown = false;
      var binderCount = 0;
      for (g = 0; g < groups.length; g++) {
        group = groups[g];
        if (group.role !== 'binder') continue;
        var binderShown;
        if (mappedBinders) {
          binderShown = runMapped(group, cards, mappedBinders, {}).shownCount;
        } else if (group.family && binderFamilies[group.family]) {
          binderShown = runCompatible(group, cards, {});
        } else {
          binderShown = 0;
          for (var i2 = 0; i2 < cards.length; i2++) {
            if (cards[i2].el.getAttribute('data-fitsearch-group-id') === group.id) {
              cards[i2].el.setAttribute('hidden', '');
            }
          }
        }
        totalShown += binderShown;
        binderCount += binderShown;
        if (binderShown > 0) {
          group.el.removeAttribute('hidden');
          anyBinderShown = true;
        } else {
          group.el.setAttribute('hidden', '');
        }
      }
      // Hide the binder step entirely when nothing is recommended.
      if (anyBinderShown) root.setAttribute('data-fitsearch-has-binders', '');
      else root.removeAttribute('data-fitsearch-has-binders');

      // Step guidance status lines.
      setStep('1', selectedSleeve
        ? 'Selected: ' + fmtSize(selectedSleeve.size) + ' sleeve'
        : (sizeMode && noSleeve
          ? 'No sleeve — pages matched to your item size'
          : (sleeveExpanded
            ? 'Pick a sleeve to continue'
            : (sizeMode
              ? 'No exact sleeve match for this size'
              : 'Tap “Match pages to this sleeve” on a sleeve to start'))));
      var step2Empty = sizeMode && noSleeve
        ? 'No pages fit this item size yet'
        : (selectedSleeve ? 'No pages hold this sleeve yet' : 'Pick a sleeve to see matching pages');
      setStep('2', selectedPage
        ? 'Selected: ' + shortTitle(selectedPage)
        : step2Empty);
      setStep('3', binderCount > 0
        ? binderCount + (binderCount === 1 ? ' binder holds' : ' binders hold') + ' your selected page'
        : 'Pick a page to see compatible binders');

      setNoSleeveUI(sizeMode, sizeMode && noSleeve);

      if (statusEl) {
        if (sizeMode) {
          statusEl.textContent = totalShown > 0
            ? 'Showing closest fits for a ' + cardShort + '×' + cardLong + 'mm card.'
            : "We don't stock a fit for that size yet — try a nearby size, or contact us about what you're looking for.";
        } else if (selectedSleeve) {
          statusEl.textContent = 'Showing matches for your ' + fmtSize(selectedSleeve.size) + ' sleeve.';
        } else if (selectedPage) {
          statusEl.textContent = 'Showing binders for your selected page.';
        } else {
          statusEl.textContent = '';
        }
      }
    }

    function search() {
      var w = parseFloat(wInput.value);
      var h = parseFloat(hInput.value);
      if (!w || !h || w <= 0 || h <= 0) {
        if (statusEl) statusEl.textContent = 'Enter both a width and height in millimetres.';
        return;
      }
      root.setAttribute('data-fitsearch-active', '');
      run(Math.min(w, h), Math.max(w, h));
    }

    // Re-run the chain after a manual pick: use the entered card size when
    // there is one, otherwise chain straight from the picked sleeve/page.
    function refresh() {
      var w = parseFloat(wInput.value);
      var h = parseFloat(hInput.value);
      root.setAttribute('data-fitsearch-active', '');
      if (w > 0 && h > 0) run(Math.min(w, h), Math.max(w, h));
      else run(null, null);
    }

    // New size = new search; drop any previous sleeve/page picks and bring
    // the step-1 results into view. withNoSleeve starts the search in
    // no-sleeve mode (presets flagged :nosleeve — e.g. a double-sleeved
    // photocard already IS sleeved, so pages match the entered size).
    function freshSearch(withNoSleeve) {
      selectedSleeve = null;
      selectedPage = null;
      sleeveExpanded = false;
      sleeveManual = false;
      noSleeve = !!withNoSleeve;
      search();
      var firstStep = root.querySelector('[data-fitsearch-step-banner="1"]');
      if (firstStep && typeof firstStep.scrollIntoView === 'function') {
        firstStep.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      freshSearch();
    });

    // Picking a sleeve re-runs pages and binders against it; picking a page
    // re-runs the binder recommendations.
    for (var c = 0; c < cards.length; c++) {
      (function (card) {
        if (!card.selectEl) return;
        card.selectEl.addEventListener('click', function (e) {
          e.preventDefault();
          if (card.role === 'page') {
            selectedPage = card;
          } else if (card === selectedSleeve) {
            // Unselect: bring the full sleeve list back, no auto re-pick.
            selectedSleeve = null;
            sleeveExpanded = true;
            sleeveManual = false;
            noSleeve = false;
          } else {
            selectedSleeve = card;
            sleeveExpanded = false;
            sleeveManual = true;
            noSleeve = false;
          }
          refresh();
          // Draw the eye to the step this pick just updated.
          pulseStep(card.role === 'page' ? '3' : (selectedSleeve ? '2' : '1'));
        });
      })(cards[c]);
    }

    var resetBtn = form.querySelector('[data-fitsearch-reset]');
    if (resetBtn) {
      resetBtn.addEventListener('click', function (e) {
        e.preventDefault();
        wInput.value = '';
        hInput.value = '';
        selectedSleeve = null;
        selectedPage = null;
        sleeveExpanded = false;
        sleeveManual = false;
        noSleeve = false;
        setNoSleeveUI(false, false);
        root.removeAttribute('data-fitsearch-active');
        resetView(root, cards, groups);
        if (statusEl) statusEl.textContent = '';
      });
    }

    if (noSleeveBtn) {
      noSleeveBtn.addEventListener('click', function (e) {
        e.preventDefault();
        if (noSleeve) {
          // Back to sleeves: show the full list, no auto re-pick.
          noSleeve = false;
          sleeveExpanded = true;
        } else {
          noSleeve = true;
          selectedSleeve = null;
          sleeveExpanded = false;
          sleeveManual = false;
        }
        refresh();
        pulseStep(noSleeve ? '2' : '1');
      });
    }

    var chips = form.querySelectorAll('[data-fitsearch-preset]');
    for (var p = 0; p < chips.length; p++) {
      chips[p].addEventListener('click', function (e) {
        e.preventDefault();
        var val = this.getAttribute('data-fitsearch-preset');
        var m = val.match(/(\d+(?:\.\d)?)\s*[x×X]\s*(\d+(?:\.\d)?)/);
        if (!m) return;
        wInput.value = m[1];
        hInput.value = m[2];
        freshSearch(this.hasAttribute('data-fitsearch-preset-nosleeve'));
      });
    }
  }

  function boot() {
    var roots = document.querySelectorAll('[data-fitsearch]');
    for (var i = 0; i < roots.length; i++) init(roots[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
